
import React, { useState } from 'react';
import { Check, Shield } from 'lucide-react';
import { SUBSCRIPTION_TIERS } from '../config/tiers';

export const PricingPage: React.FC = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  
  const tiers = Object.entries(SUBSCRIPTION_TIERS).filter(([key]) => key !== 'free');
  
  return (
    <div className="h-full overflow-y-auto py-10 px-6 bg-[#F9F8F4]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-black text-stone-900 mb-6 tracking-tight">
            Achieve Band 7+ in <span className="text-[#C5A059]">60 Days</span>
          </h1>
          <p className="text-xl text-stone-600 mb-8">
            Join 12,487 warriors who've reached their target score
          </p>
          
          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 bg-white p-2 rounded-full border-2 border-stone-200 shadow-sm">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-3 rounded-full font-bold text-sm transition-all ${
                billingCycle === 'monthly' 
                  ? 'bg-stone-900 text-white' 
                  : 'text-stone-600'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-3 rounded-full font-bold text-sm transition-all relative ${
                billingCycle === 'annual' 
                  ? 'bg-[#C5A059] text-stone-900' 
                  : 'text-stone-600'
              }`}
            >
              Annual
              <span className="absolute -top-3 -right-3 bg-red-500 text-white text-[10px] px-2 py-1 rounded-full font-black animate-bounce">
                SAVE 43%
              </span>
            </button>
          </div>
        </div>
        
        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-20">
          {tiers.map(([key, tier]) => {
            const isPopular = key === 'premium';
            const price = tier.price[billingCycle];
            const monthlyEquivalent = billingCycle === 'annual' 
              ? Math.round(price / 12) 
              : price;
            
            return (
              <div
                key={key}
                className={`relative bg-white rounded-3xl p-10 border-4 ${
                  isPopular 
                    ? 'border-[#C5A059] shadow-2xl scale-105 z-10' 
                    : 'border-stone-200'
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#C5A059] text-stone-900 px-6 py-2 rounded-full font-black text-sm">
                    MOST POPULAR
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <div className="text-6xl mb-4">{tier.badge.icon}</div>
                  <h3 className="text-3xl font-black text-stone-900 mb-2">{tier.name}</h3>
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-5xl font-black text-[#C5A059]">
                      ${monthlyEquivalent}
                    </span>
                    <span className="text-stone-500">/month</span>
                  </div>
                  {billingCycle === 'annual' && (
                    <p className="text-sm text-stone-500 mt-2">
                      Billed ${price} annually
                    </p>
                  )}
                </div>
                
                <ul className="space-y-4 mb-8">
                  {Object.entries(tier.features)
                    .filter(([_, value]) => value !== false && value !== 0)
                    .map(([feature, value]) => (
                      <li key={feature} className="flex items-start gap-3">
                        <Check className="text-[#C5A059] flex-shrink-0 mt-1" size={20} />
                        <span className="text-stone-700 text-sm">
                          {feature.replace(/([A-Z])/g, ' $1').trim()}: {' '}
                          <strong>
                            {typeof value === 'boolean' ? 'Yes' : value}
                          </strong>
                        </span>
                      </li>
                    ))}
                </ul>
                
                <button
                  onClick={() => {
                    // Placeholder for Stripe
                    console.log(`Selected ${key} - ${billingCycle}`);
                    alert("Payment Gateway Integration Pending - Phase 2");
                  }}
                  className={`w-full py-5 rounded-xl font-black text-lg transition-all hover:scale-105 ${
                    isPopular
                      ? 'bg-[#C5A059] text-stone-900 shadow-lg'
                      : 'bg-stone-900 text-white'
                  }`}
                >
                  START {billingCycle === 'annual' ? 'ANNUAL' : 'MONTHLY'} PLAN
                </button>
              </div>
            );
          })}
        </div>
        
        {/* Money-Back Guarantee */}
        <div className="text-center bg-white rounded-2xl p-12 border-2 border-stone-200 max-w-3xl mx-auto mb-10">
          <Shield className="mx-auto text-[#C5A059] mb-6" size={64} />
          <h3 className="text-3xl font-black mb-4">Risk-Free 7-Day Money Back Guarantee</h3>
          <p className="text-stone-600 text-lg">
            Not satisfied? Get a full refund within 7 days, no questions asked.
          </p>
        </div>
      </div>
    </div>
  );
};
